import DataTable from "../components/common/DataTable";
import { modals } from "@mantine/modals";
import { Button, TextInput, Modal, Loader, Switch, Text, Group } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { Search, X } from "lucide-react";
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Head from "../components/common/Head";
import EpisodeModal from "../components/EpisodeModal";
import ContentTable from "../components/movies/Table";
import useValidate from "../hooks/useValidate";
import {
  deleteComment,
  deleteEpisode,
  getEpisodeComments,
  updateComment,
} from "../utils/api";
import { useEpisodesBySeasonId, useSeasonById } from "../utils/queries";
import useStore from "../utils/store";

const Episodes = () => {
  
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useStore((state) => state.user);

  const [modal, setModal] = React.useState(false);
  const [modal2, setModal2] = React.useState(false);
  const [update, setUpdate] = React.useState({});
  const [commentsModal, setCommentsModal] = React.useState(false);
  const [selectedEpisode, setSelectedEpisode] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [commentPagination, setCommentPagination] = React.useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Episodes pagination & filters
  const [pagination, setPagination] = React.useState({
    current: parseInt(searchParams.get("page") || "1", 10),
    pageSize: 10,
  });
  const [searchText, setSearchText] = React.useState(
    searchParams.get("search") || ""
  );
  const [inputText, setInputText] = React.useState(
    searchParams.get("search") || ""
  );
  const [isEnableFilter, setIsEnableFilter] = React.useState(
    searchParams.get("isEnable") === "true" ? true : undefined
  );

  const { seasonId } = useParams();
  const { data: season } = useSeasonById(seasonId);
  const {
    data: episodes,
    isLoading: loading2,
    refetch: refetch2,
  } = useEpisodesBySeasonId(
    seasonId,
    pagination.current,
    pagination.pageSize,
    searchText,
    isEnableFilter
  );
  useValidate();

  if (!user?.getAllSeries) {
    return (
      <Text c="dimmed">You don't have permission to view episodes</Text>
    );
  }

  const handleDelete = async (episodeId) => {
    const [success] = await deleteEpisode({ episodeId });
    if (success) {
      notifications.show({ message: "Episode deleted successfully", color: "green" });
      refetch2();
    }
  };

  const fetchComments = async (episodeId, page, pageSize) => {
    setLoading(true);
    try {
      const [success, data] = await getEpisodeComments(
        episodeId,
        page,
        pageSize
      );
      if (success) {
        setComments(data.data || []);
        setCommentPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: data.metadata?.totalData || 0,
        }));
      } else {
        notifications.show({ message: "Failed to load comments", color: "red" });
      }
    } catch (error) {
      notifications.show({ message: "An error occurred while loading comments", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleViewComments = async (episode) => {
    setSelectedEpisode(episode);
    setCommentsModal(true);
    await fetchComments(
      episode._id,
      commentPagination.current,
      commentPagination.pageSize
    );
  };

  const handleCommentPaginationChange = async (page, pageSize) => {
    await fetchComments(selectedEpisode._id, page, pageSize);
  };

  const handleUpdateComment = async (values) => {
    setActionLoading(true);
    try {
      const [success] = await updateComment(values._id, {
        isApproved: values.isApproved,
        text: values.text,
      });
      if (success) {
        notifications.show({ message: "Comment updated successfully", color: "green" });
        await fetchComments(
          selectedEpisode._id,
          commentPagination.current,
          commentPagination.pageSize
        );
      } else {
        notifications.show({ message: "Failed to update comment", color: "red" });
      }
    } catch (error) {
      notifications.show({ message: "An error occurred while updating the comment", color: "red" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setActionLoading(true);
    try {
      const [success] = await deleteComment(commentId);
      if (success) {
        notifications.show({ message: "Comment deleted successfully", color: "green" });
        await fetchComments(
          selectedEpisode._id,
          commentPagination.current,
          commentPagination.pageSize
        );
      } else {
        notifications.show({ message: "Failed to delete comment", color: "red" });
      }
    } catch (error) {
      notifications.show({ message: "An error occurred while deleting the comment", color: "red" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleEpisodesPaginationChange = (page, pageSize) => {
    setPagination({ current: page, pageSize: pageSize || pagination.pageSize });
    setSearchParams({
      page: page.toString(),
      ...(searchText && { search: searchText }),
      ...(isEnableFilter !== undefined && {
        isEnable: String(isEnableFilter),
      }),
    });
  };

  const handleSearch = () => {
    setSearchText(inputText);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    setSearchParams({
      page: "1",
      ...(inputText && { search: inputText }),
      ...(isEnableFilter !== undefined && {
        isEnable: String(isEnableFilter),
      }),
    });
  };

  const handleSearchClear = () => {
    setInputText("");
    setSearchText("");
    setPagination({ current: 1, pageSize: pagination.pageSize });
    setSearchParams({
      page: "1",
      ...(isEnableFilter !== undefined && {
        isEnable: String(isEnableFilter),
      }),
    });
  };

  const handleIsEnableFilterChange = (checked) => {
    const newFilter = checked ? true : undefined;
    setIsEnableFilter(newFilter);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    setSearchParams({
      page: "1",
      ...(searchText && { search: searchText }),
      ...(newFilter !== undefined && { isEnable: String(newFilter) }),
    });
  };

  const commentColumns = [
    {
      title: "User",
      dataIndex: ["user", "email"],
      key: "user",
    },
    {
      title: "Comment",
      dataIndex: "text",
      key: "text",
    },
    {
      title: "Approved",
      dataIndex: "isApproved",
      key: "isApproved",
      render: (isApproved, record) => (
        <Switch
          checked={isApproved}
          onChange={(e) => {
            handleUpdateComment({
              ...record,
              isApproved: e.currentTarget.checked,
            });
          }}
          disabled={actionLoading}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <span onClick={() => modals.openConfirmModal({ title: "Are you sure you want to delete this comment?", labels: { confirm: "Yes", cancel: "No" }, onConfirm: () => handleDeleteComment(record._id) })}>
          <Button color="red" disabled={actionLoading}>
            Delete
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      
      <Head
        title={`${season?.name} - Episodes`}
        button={user?.addSeries ? "Add Episode" : null}
        onClick={user?.addSeries ? () => setModal(true) : null}
      />

      {/* Search and filters */}
      <Group mb="md" wrap="wrap">
        <TextInput
          placeholder="Search episodes..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          rightSection={
            inputText ? (
              <X size={14} style={{ cursor: "pointer" }} onClick={() => { setInputText(""); handleSearchClear(); }} />
            ) : null
          }
          style={{ maxWidth: 400 }}
        />
        <Button leftSection={<Search size={14} />} onClick={handleSearch}>Search</Button>
        <Group gap="xs">
          <span>Enabled only</span>
          <Switch
            checked={!!isEnableFilter}
            onChange={(e) => handleIsEnableFilterChange(e.currentTarget.checked)}
          />
        </Group>
        <Button variant="default" onClick={handleSearchClear}>Reset</Button>
      </Group>

      <ContentTable
        data={episodes?.data}
        loading={loading2}
        setModal2={user?.updateSeries ? setModal2 : null}
        setUpdate={user?.updateSeries ? setUpdate : null}
        reload={refetch2}
        episodes
        id={seasonId}
        canEdit={user?.updateSeries}
        canDelete={user?.deleteSeries}
        onDelete={handleDelete}
        permissions={{
          deleteSeries: user?.deleteSeries,
          updateSeries: user?.updateSeries,
        }}
        onViewComments={handleViewComments}
        exportCsvData={episodes?.data}
        exportCsvFilename={`episodes-${season?.name || seasonId}-${new Date()
          .toISOString()
          .slice(0, 10)}.csv`}
        pagination={{
          total: episodes?.metadata?.totalData,
          current: pagination.current,
          pageSize: pagination.pageSize,
          onChange: handleEpisodesPaginationChange,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        currentPage={pagination.current}
      />
      <EpisodeModal
        isOpen={modal}
        onClose={() => setModal(false)}
        seasonId={seasonId}
        onSuccess={refetch2}
      />
      <EpisodeModal
        isOpen={modal2}
        onClose={() => setModal2(false)}
        seasonId={seasonId}
        onSuccess={refetch2}
        episodeToEdit={update}
      />
      <Modal
        title={`Comments - ${selectedEpisode?.name || ""}`}
        opened={commentsModal}
        onClose={() => {
          setCommentsModal(false);
          setSelectedEpisode(null);
          setComments([]);
          setCommentPagination({
            current: 1,
            pageSize: 10,
            total: 0,
          });
        }}
        width={800}
        footer={null}
      >
        <Loader visible={loading || actionLoading}>
          <DataTable
            columns={commentColumns}
            dataSource={comments}
            loading={loading}
            rowKey="_id"
            pagination={{
              current: commentPagination.current,
              pageSize: commentPagination.pageSize,
              total: commentPagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} comments`,
              onChange: handleCommentPaginationChange,
            }}
          />
        </Loader>
      </Modal>
    </div>
  );
};

export default Episodes;
